/**
 * onboarding-slots — TypeScript mirror of the Go
 * `internal/identity/onboarding/slots.go` canonical text table.
 *
 * # Why mirror, not generate
 *
 * The backend recommender names slots (`device_hint.mac`,
 * `reason.platform_passkey_capable`, etc.) but never resolves
 * them to text — that's the presentation layer's job. This file
 * IS the presentation layer for the English locale, consumed by
 * `RecommendedDeviceChip` and every future surface that renders
 * an onboarding recommendation.
 *
 * # Drift contract
 *
 * The exported `ONBOARDING_SLOT_TEXT` map MUST keep parity with
 * `canonicalSlotText` in the Go source. Adding a slot key on the
 * backend without adding it here renders an empty string in the
 * chip — a UX bug. A future hardening pass will codegen this
 * file from the Go source; for now the parity is enforced by
 * convention + adversarial review.
 *
 * Tenant overrides are NOT applied here. Per Charter N2 (snapshot
 * at emit) the audit log carries the slot key, not the rendered
 * text — tenants who customize a slot get the override applied
 * at the microcopy resolution layer (future work; today the
 * canonical English text is the only text any user sees).
 */

/**
 * SlotKey is the canonical onboarding slot-key vocabulary.
 * Kept as a string-union type rather than an enum so the
 * compiler can detect typos at every call site without
 * forcing an import of a runtime value.
 */
export type SlotKey =
  // Device hint slots — short chip text.
  | "device_hint.mac"
  | "device_hint.windows"
  | "device_hint.iphone"
  | "device_hint.ipad"
  | "device_hint.android"
  | "device_hint.chromeos"
  | "device_hint.linux"
  | "device_hint.azure_vm"
  | "device_hint.vdi"
  | "device_hint.kiosk"
  | "device_hint.private_mode"
  | "device_hint.insecure_context"
  | "device_hint.no_webauthn"
  | "device_hint.unknown"
  // Frontline-worker routing label. When the cascade returns
  // this slot the chip names the user (not the device) because
  // the routing decision is user-class, not device-class.
  | "device_hint.frontline_worker"
  // Device-only variants — used when the recommendation is NOT
  // the platform authenticator, so the chip doesn't advertise
  // "with Hello / Touch ID / Face ID" next to a phone-QR or
  // security-key reason line.
  | "device_hint.windows_plain"
  | "device_hint.mac_plain"
  | "device_hint.ipad_plain"
  // Primary-method reason slots.
  | "reason.platform_passkey_capable"
  | "reason.phone_qr_fragile"
  | "reason.phone_qr_linux_no_tpm"
  | "reason.phone_qr_desktop"
  | "reason.security_key_executive"
  | "reason.security_key_linux_firefox"
  | "reason.tap_frontline"
  | "reason.tap_outstanding"
  | "reason.fragile_redirect_vm"
  | "reason.fragile_redirect_vdi"
  | "reason.fragile_redirect_kiosk"
  | "reason.none_no_webauthn"
  | "reason.none_insecure"
  | "reason.none_private"
  // Tenant-policy refinement reasons — fire when the tenant's
  // WebAuthn AuthenticatorAttachment policy overrides the
  // device-class default. Pairs with method=platform_passkey,
  // method=phone_qr, or method=none depending on which side the
  // policy enforces.
  | "reason.policy_platform"
  | "reason.policy_cross_platform"
  | "reason.policy_no_method"
  // F-3 — frontline routing reason. Layer-5 fires when the user
  // is marked frontline AND the tenant has frontline_enabled.
  | "reason.frontline_route_pending"
  // DD-NEXT-7 — layer-6 outcome-telemetry rule promoted an
  // alternative because it completes sign-in more often for this
  // team on devices like the user's.
  | "reason.autotuned_fleet_outcomes"
  // Alternative-method reason slots.
  | "reason_alt.platform_passkey"
  | "reason_alt.phone_qr"
  | "reason_alt.security_key"
  | "reason_alt.tap"
  // DD-7 celebration slots — post-enrol per-OS posture copy.
  | "celebration.platform_mac"
  | "celebration.platform_windows"
  | "celebration.platform_iphone"
  | "celebration.platform_ipad"
  | "celebration.platform_android"
  | "celebration.platform_chromeos"
  | "celebration.platform_linux"
  | "celebration.platform_generic"
  | "celebration.phone_qr"
  | "celebration.security_key"
  // DD-15 "what you're enabling" pre-ceremony posture explainer.
  | "what_you_enable.platform_passkey"
  | "what_you_enable.phone_qr"
  | "what_you_enable.security_key"
  | "what_you_enable.tap"
  | "what_you_enable.fragile_redirect"
  // DD-3 narrator slots — what's about to happen on screen.
  | "narrator.platform_mac"
  | "narrator.platform_windows"
  | "narrator.platform_iphone"
  | "narrator.platform_ipad"
  | "narrator.platform_android"
  | "narrator.platform_chromeos"
  | "narrator.platform_linux"
  | "narrator.platform_generic"
  | "narrator.phone_qr"
  | "narrator.security_key";

/**
 * ONBOARDING_SLOT_TEXT mirrors `canonicalSlotText` in the Go
 * source. Every key here MUST have a matching entry in the Go
 * map and vice versa.
 *
 * The English defaults are deliberately concrete and short:
 * device hints fit inside a small chip; reasons read as one
 * sentence the user can absorb without re-reading.
 */
export const ONBOARDING_SLOT_TEXT: Record<SlotKey, string> = {
  // Device hints — short chip text.
  "device_hint.mac": "Mac with Touch ID",
  "device_hint.windows": "Windows 11 with Hello",
  "device_hint.iphone": "iPhone",
  "device_hint.ipad": "iPad with Face ID or Touch ID",
  "device_hint.android": "Android phone",
  "device_hint.chromeos": "Chromebook",
  "device_hint.linux": "Linux desktop",
  "device_hint.azure_vm": "Azure Virtual Machine",
  "device_hint.vdi": "Virtual desktop session",
  "device_hint.kiosk": "Shared workstation",
  "device_hint.private_mode": "Private browsing window",
  "device_hint.insecure_context": "Insecure connection",
  "device_hint.no_webauthn": "Unsupported browser",
  "device_hint.unknown": "This device",
  "device_hint.frontline_worker": "Badge sign-in",
  "device_hint.windows_plain": "Windows 11",
  "device_hint.mac_plain": "Mac",
  "device_hint.ipad_plain": "iPad",

  // Primary-method reasons.
  "reason.platform_passkey_capable":
    "We'll use your built-in biometric for sign-in — one prompt, no password.",
  "reason.phone_qr_fragile":
    "This device can't store a passkey directly. Your phone will hold it instead.",
  "reason.phone_qr_linux_no_tpm":
    "Linux without a TPM can't store a platform passkey. We'll pair with your phone.",
  "reason.phone_qr_desktop":
    "Set up your passkey on your phone, then sign in here with just your fingerprint or face.",
  "reason.security_key_executive":
    "Your workspace requires a hardware security key for sign-in.",
  "reason.security_key_linux_firefox":
    "Firefox on Linux can't pair with a phone. A security key works directly.",
  "reason.tap_frontline":
    "Your shift lead will give you a short code. Type it on the screen to start.",
  "reason.tap_outstanding":
    "You have a one-time code from your administrator. Type it here to continue.",
  "reason.fragile_redirect_vm":
    "Virtual machines can't store passkeys directly. We'll set yours up on your phone in 30 seconds.",
  "reason.fragile_redirect_vdi":
    "Virtual desktop sessions can't store passkeys directly. We'll route you through your phone.",
  "reason.fragile_redirect_kiosk":
    "Shared devices shouldn't keep your sign-in. We'll set it up on your phone instead.",
  "reason.none_no_webauthn":
    "This browser doesn't support passkeys. Open this page on a modern browser to continue.",
  "reason.none_insecure":
    "This page must be opened over HTTPS for sign-in to work.",
  "reason.none_private":
    "Private browsing blocks passkey sign-in. Open this page in a regular window.",

  // Tenant-policy refinement copy — names the organization as
  // the driver so the user doesn't blame their device.
  "reason.policy_platform":
    "Your organization requires this device's built-in unlock for sign-in.",
  "reason.policy_cross_platform":
    "Your organization requires sign-in via your phone or a hardware security key.",
  "reason.policy_no_method":
    "Your organization's sign-in policy can't be satisfied on this device. Use a device with a built-in fingerprint, face, or PIN unlock — or ask your administrator for help.",

  // Frontline routing copy. Verb-first, 5th-grade, names the
  // person on the floor (shift lead), not "administrator". No
  // "pair" / "kiosk" / "frontline worker" jargon (UX-P0-B).
  "reason.frontline_route_pending":
    "Tap your badge on the reader to sign in. No badge yet? Ask your shift lead — they can set you up.",

  // DD-NEXT-7 — text MUST match Go canonicalSlotText exactly
  // (the slots-drift test enforces parity).
  "reason.autotuned_fleet_outcomes":
    "This works best for your team on devices like yours, so we've made it the default.",

  // Alternative-method reasons.
  "reason_alt.platform_passkey": "Use the biometric built into this device.",
  "reason_alt.phone_qr": "Use your phone to scan a QR code.",
  "reason_alt.security_key": "Use a hardware security key.",
  "reason_alt.tap": "Use a one-time code from your administrator.",

  // DD-7 celebration — per-OS post-enrol posture copy. Mirrors
  // canonicalSlotText in `backend/internal/identity/onboarding/
  // slots.go`. Both must update together; a future codegen pass
  // will eliminate the parallel-table drift risk.
  "celebration.platform_mac":
    "You're set up with Touch ID on this Mac. " +
    "Next time, just enter your email — Touch ID handles the rest. No password, ever.",
  "celebration.platform_windows":
    "Windows Hello is set up on this PC. " +
    "Next time, just enter your email — Hello handles the rest. No password, ever.",
  "celebration.platform_iphone":
    "Your iPhone is set up with Face ID. " +
    "Next time, just enter your email — Face ID signs you in. No password, ever.",
  "celebration.platform_ipad":
    "Your iPad is set up for passkey sign-in. " +
    "Next time, just enter your email — Face ID or Touch ID handles the rest. No password, ever.",
  "celebration.platform_android":
    "Your Android is set up with biometric sign-in. " +
    "Next time, just enter your email — your phone handles the rest. No password, ever.",
  "celebration.platform_chromeos":
    "Your Chromebook is set up for passkey sign-in. " +
    "Next time, just enter your email — your screen unlock handles the rest. No password, ever.",
  "celebration.platform_linux":
    "Your Linux desktop is set up for passkey sign-in. " +
    "Next time, just enter your email — your TPM handles the rest. No password, ever.",
  "celebration.platform_generic":
    "Your passkey is saved on this device. " +
    "Next time, just enter your email — your device handles the rest. No password, ever.",
  "celebration.phone_qr":
    "Your passkey is saved on your phone. " +
    "Next time, your phone handles sign-in — over Bluetooth if it's nearby, " +
    "or by scanning a fresh QR code. No password, ever.",
  "celebration.security_key":
    "Your security key is registered. " +
    "Next time, plug in or tap your key when prompted. No password, ever.",

  // DD-15 — pre-ceremony posture explainer. Mirrors the
  // canonicalSlotText entries in slots.go.
  "what_you_enable.platform_passkey":
    "No password, ever. Can't be phished. Tied to this device's biometric — " +
    "works the instant you're holding it.",
  "what_you_enable.phone_qr":
    "No password, ever. Can't be phished. Lives on your phone — " +
    "works on any device when it's nearby.",
  "what_you_enable.security_key":
    "No password, ever. Can't be phished. Lives on your physical key — " +
    "works on any device when you have the key.",
  "what_you_enable.tap":
    "One-time bootstrap. You'll type this code here, then set up a permanent passkey on your phone. " +
    "The code only works once.",
  "what_you_enable.fragile_redirect":
    "We'll route you to your phone to set up the passkey there. " +
    "Your phone holds the key; this device uses it.",

  // DD-3 narrator — what's about to happen on screen. Mirrors
  // the canonicalSlotText in slots.go. Present-tense to match
  // the reading beat of the user clicking Continue and the OS
  // modal appearing in the same moment.
  "narrator.platform_mac":
    "macOS will prompt for Touch ID — confirm with your fingerprint.",
  "narrator.platform_windows":
    "Windows is opening Hello — confirm with your PIN, fingerprint, or face.",
  "narrator.platform_iphone":
    "iOS will ask for Face ID — look at your phone to confirm.",
  "narrator.platform_ipad":
    "iPadOS will ask for Face ID or Touch ID — confirm on your iPad.",
  "narrator.platform_android":
    "Android will ask for your screen lock — confirm with your biometric or PIN.",
  "narrator.platform_chromeos":
    "Chromebook will ask for your screen lock — confirm to continue.",
  "narrator.platform_linux":
    "Your browser will prompt for a passkey — confirm with your screen lock or fingerprint.",
  "narrator.platform_generic":
    "Your device will ask for a passkey — confirm with your fingerprint, face, or screen lock.",
  "narrator.phone_qr":
    "We're opening a QR code — scan it with your phone's camera, then confirm with your fingerprint or face.",
  "narrator.security_key":
    "Plug in or tap your security key when it lights up.",
};

/**
 * resolveSlot returns the English text for a slot key. Returns
 * the empty string for an unknown key — the chip component
 * defensively returns null in that case rather than rendering
 * an empty chip.
 *
 * When a key is not found, emits a console warning so the drift
 * is visible during QA + production monitoring. (Adversarial-
 * review Q7: silent empty-string failures hide backend additions
 * that the frontend hasn't been rebuilt against.)
 */
export function resolveSlot(key: SlotKey | string): string {
  const text = ONBOARDING_SLOT_TEXT[key as SlotKey];
  if (text === undefined) {
    if (typeof console !== "undefined" && typeof console.warn === "function") {
      console.warn(
        `[onboarding-slots] unknown slot key %o — backend added a slot ` +
          `the frontend bundle doesn't know about. Rebuild required.`,
        key,
      );
    }
    return "";
  }
  return text;
}

/**
 * Method names the onboarding wizard's primary CTA. Mirrors the
 * Go `onboarding.Method` enum.
 */
export type RecommendedMethod =
  | "platform_passkey"
  | "phone_qr"
  | "security_key"
  | "tap"
  | "fragile_redirect"
  | "none";

/**
 * Alternative is one ranked fallback under a primary
 * recommendation.
 */
export interface RecommendedAlternative {
  method: RecommendedMethod;
  reasonSlot: SlotKey;
}

/**
 * RecommendedDefault is the typed shape returned by
 * `POST /api/onboarding/v1/onboarding/recommended-default` and
 * mirrors the Go `onboarding.RecommendedDefault` struct.
 *
 * `deviceHintSlot` and `reasonSlot` are the canonical slot keys;
 * the chip component resolves them via `resolveSlot()` at render
 * time. Storing the key (not the text) on the wire is what makes
 * a future tenant copy override painless.
 */
export interface RecommendedDefault {
  deviceHintSlot: SlotKey;
  method: RecommendedMethod;
  reasonSlot: SlotKey;
  alternatives: RecommendedAlternative[];
  provider: string;
}

/**
 * EnrolledTransport names the transport string the wizard
 * reducer parks on after a successful ceremony. Same wire
 * vocabulary the existing `successHeadline()` /
 * `successCoaching()` helpers switched on.
 */
export type EnrolledTransport =
  | "this_device"
  | "other_device"
  | "security_key"
  | "internal"
  | "hybrid"
  | "usb"
  | "nfc"
  | "ble";

/**
 * WizardTransport is the narrow subset of EnrolledTransport
 * that the enrollment wizard's TransportChooser actually
 * supports as a `state.choice` value — the same 3-value union
 * `PasskeyTransport` from `@pureid/auth-core/storage/
 * preferred-transport.ts`. Duplicated here as a stable string
 * union so onboarding-slots stays a pure lib (no runtime
 * import) and so the return type of `methodToTransport`
 * narrows cleanly.
 */
export type WizardTransport = "this_device" | "other_device" | "security_key";

/**
 * narratorSlotFor picks the DD-3 narrator slot from the
 * transport the user just committed to + the device-hint slot
 * the DD-2 recommender already detected. The narrator renders
 * during `passkey_running` (the brief window between user click
 * and OS prompt opening), so transport is always known and
 * deviceHintSlot is always populated (the chip already
 * resolved it).
 *
 * Mapping:
 *   - this_device / internal → narrator.platform_<os>
 *     where <os> derives from the device-hint slot.
 *     Falls back to narrator.platform_generic when the OS
 *     family is unclassified (the user still gets a usable
 *     "your device will prompt" message).
 *   - other_device / hybrid → narrator.phone_qr
 *   - security_key / usb / nfc / ble → narrator.security_key
 *
 * Always returns a SlotKey; never null. The caller can
 * unconditionally pass the result to resolveSlot().
 */
export function narratorSlotFor(
  transport: EnrolledTransport | string,
  deviceHintSlot?: SlotKey | string,
): SlotKey {
  switch (transport) {
    case "other_device":
    case "hybrid":
      return "narrator.phone_qr";
    case "security_key":
    case "usb":
    case "nfc":
    case "ble":
      return "narrator.security_key";
    case "this_device":
    case "internal":
    default:
      switch (deviceHintSlot) {
        case "device_hint.mac":
          return "narrator.platform_mac";
        case "device_hint.windows":
          return "narrator.platform_windows";
        case "device_hint.iphone":
          return "narrator.platform_iphone";
        case "device_hint.ipad":
          return "narrator.platform_ipad";
        case "device_hint.android":
          return "narrator.platform_android";
        case "device_hint.chromeos":
          return "narrator.platform_chromeos";
        case "device_hint.linux":
          return "narrator.platform_linux";
        default:
          return "narrator.platform_generic";
      }
  }
}

/**
 * methodToTransport maps the recommender's `RecommendedMethod`
 * to the wizard's `WizardTransport` vocabulary so DD-5 can
 * escalate from the failed transport to the next alternative
 * without each surface re-deriving the mapping.
 *
 * Returns null for methods that aren't wizard transports
 * (`tap` routes to the TAP flow, `fragile_redirect` to the
 * fragile-device surface, `none` is hard-blocked). Callers
 * filter null when picking the next escalation target.
 */
export function methodToTransport(
  method: RecommendedMethod,
): WizardTransport | null {
  switch (method) {
    case "platform_passkey":
      return "this_device";
    case "phone_qr":
      return "other_device";
    case "security_key":
      return "security_key";
    case "tap":
    case "fragile_redirect":
    case "none":
    default:
      return null;
  }
}

/**
 * transportToMethod is the inverse of methodToTransport.
 * Returns the recommender-vocabulary method string for a
 * wizard transport, used by DD-5 v2 to populate the
 * `original_method` / `escalated_to_method` audit-row fields
 * at the moment the escalation dispatch fires (the wizard
 * state holds transports; the audit row holds methods).
 *
 * Total over WizardTransport — no nullable return because
 * every WizardTransport corresponds to exactly one method.
 */
export function transportToMethod(
  transport: WizardTransport,
): RecommendedMethod {
  switch (transport) {
    case "this_device":
      return "platform_passkey";
    case "other_device":
      return "phone_qr";
    case "security_key":
      return "security_key";
  }
}

/**
 * synthesizeOptimisticRecommendation builds a minimal client-side
 * RecommendedDefault from the inferred environment + UA hint, so
 * the chip + illustration + picker can render *before* the server-
 * side recommendation arrives.
 *
 * # Why this exists
 *
 * For Mac / Win / Chromebook users (the common case), the chip +
 * illustration appear only after the
 * `POST /onboarding/v1/onboarding/recommended-default` fetch
 * resolves — typically 200-800ms on a fresh load. The EmailForm
 * renders chip-less during that window and the chrome shifts as
 * each surface pops in. The honest UX is "render the chip with
 * what we already know locally, swap to the server's verdict when
 * it lands."
 *
 * The synthesis cannot know:
 *   - the tenant's attachment policy (server-only)
 *   - the tenant's active playbook (server-only)
 *   - the user's outstanding-TAP state (post-Identify only)
 *
 * Any of those would override the device-class default. So a Mac
 * user on a cross-platform-only tenant briefly sees a Touch ID
 * chip before the server swaps it to phone-QR — a small flicker,
 * much smaller than the no-chip → chip transition we're replacing.
 *
 * # Provider tag
 *
 * The synthesized result carries `provider: "client-optimistic"`
 * so audit / telemetry can distinguish it from server results.
 * Once the server result arrives the consumer should replace,
 * not merge.
 *
 * Returns undefined when the inferred class is hard-blocked
 * (private_mode / insecure_context / no_webauthn) — those have
 * environment-specific surfacing (EnvironmentPreflightBanner)
 * that's more useful than a "MethodNone" chip.
 */
export function synthesizeOptimisticRecommendation(
  inferredClass: string,
  ua: { os: string; browser: string },
): RecommendedDefault | undefined {
  // Hard-blocked classes — defer entirely to the server (or to
  // EnvironmentPreflightBanner). The chip would just say "this
  // browser doesn't support passkeys" which we already surface
  // more prominently elsewhere.
  if (
    inferredClass === "private_mode" ||
    inferredClass === "insecure_context" ||
    inferredClass === "no_webauthn"
  ) {
    return undefined;
  }

  const deviceHint = deviceHintSlotForUa(ua);

  // Method + reason from the inferred class, mirroring the
  // server cascade's device-class default + browser refinement
  // (Linux+Firefox → security_key).
  if (inferredClass === "vdi_class") {
    return {
      deviceHintSlot: "device_hint.vdi",
      method: "fragile_redirect",
      reasonSlot: "reason.fragile_redirect_vdi",
      alternatives: [
        { method: "security_key", reasonSlot: "reason_alt.security_key" },
        { method: "tap", reasonSlot: "reason_alt.tap" },
      ],
      provider: "client-optimistic",
    };
  }
  if (inferredClass === "fragile_vm") {
    return {
      deviceHintSlot: "device_hint.azure_vm",
      method: "fragile_redirect",
      reasonSlot: "reason.fragile_redirect_vm",
      alternatives: [
        { method: "security_key", reasonSlot: "reason_alt.security_key" },
        { method: "tap", reasonSlot: "reason_alt.tap" },
      ],
      provider: "client-optimistic",
    };
  }
  if (inferredClass === "shared_or_kiosk") {
    return {
      deviceHintSlot: "device_hint.kiosk",
      method: "fragile_redirect",
      reasonSlot: "reason.fragile_redirect_kiosk",
      alternatives: [
        { method: "tap", reasonSlot: "reason_alt.tap" },
      ],
      provider: "client-optimistic",
    };
  }
  if (inferredClass === "fragile_partial") {
    // Linux + Firefox special-cases to security_key (mirrors
    // applyBrowserRefinement in deterministic.go).
    if (ua.os === "Linux" && ua.browser === "Firefox") {
      return {
        deviceHintSlot: "device_hint.linux",
        method: "security_key",
        reasonSlot: "reason.security_key_linux_firefox",
        alternatives: [
          { method: "phone_qr", reasonSlot: "reason_alt.phone_qr" },
        ],
        provider: "client-optimistic",
      };
    }
    return {
      deviceHintSlot: deviceHint,
      method: "phone_qr",
      reasonSlot: "reason.phone_qr_fragile",
      alternatives: [
        { method: "security_key", reasonSlot: "reason_alt.security_key" },
      ],
      provider: "client-optimistic",
    };
  }

  // modern_capable / unknown — both default to platform_passkey.
  // Server cascade behaves the same; this is the high-volume
  // happy path that benefits most from the optimistic chip.
  return {
    deviceHintSlot: deviceHint,
    method: "platform_passkey",
    reasonSlot: "reason.platform_passkey_capable",
    alternatives: [
      { method: "phone_qr", reasonSlot: "reason_alt.phone_qr" },
      { method: "security_key", reasonSlot: "reason_alt.security_key" },
    ],
    provider: "client-optimistic",
  };
}

/**
 * deviceHintSlotForUa picks the per-OS device hint slot from a
 * UA parse. Internal helper for synthesizeOptimisticRecommendation;
 * mirrors the OS-family branch of the server's `refineDeviceHint`.
 *
 * Note on iPad-in-desktop-mode: iPad with the desktop-Safari
 * setting sends a Mac UA, indistinguishable without a touch-probe.
 * It classifies here as "Mac" — matches the server's behaviour.
 */
function deviceHintSlotForUa(ua: { os: string; browser: string }): SlotKey {
  switch (ua.os) {
    case "macOS":
      return "device_hint.mac";
    case "Windows":
      return "device_hint.windows";
    case "iOS":
      return "device_hint.iphone";
    case "iPadOS":
      return "device_hint.ipad";
    case "Android":
      return "device_hint.android";
    case "Linux":
      return "device_hint.linux";
    default:
      return "device_hint.unknown";
  }
}

/**
 * reconcileRecommendationWithCapability returns a recommendation
 * that's known-viable given the *client's* probed capability —
 * even when the server-side recommender (which classifies from
 * the UA header alone) couldn't see a per-session capability gap
 * like "Bluetooth is off."
 *
 * # Why this exists
 *
 * The server-side classifier (`deviceclass.go`) maps UA →
 * device class. It cannot see the live capability probe result
 * (capability lives client-side only; sending it as a request
 * input is rejected by the SSO charter — Identify must be
 * recoverable from UA + tenant policy alone). So a Linux+Chrome
 * user without Google sign-in classifies as `fragile_partial`
 * and the recommender returns `phone_qr`. If that user happens
 * to have Bluetooth off, phone-QR is unreachable AND the chip
 * + illustration + transport-picker initial selection all point
 * at it. The user clicks, ceremony hangs, picker eventually
 * disables the card via its own probe — but only after a wasted
 * round-trip.
 *
 * This reconciler runs in the SPA: when the server says phone_qr
 * but capability says `hybridFeasibility === false`, swap to the
 * best surviving alternative (security_key if it's in alts;
 * MethodNone with a Bluetooth-off explainer if not).
 *
 * Symmetric for the rarer platform_passkey + platform=false case
 * (server's UA classifier put the user in modern_capable but the
 * device actually has no platform authenticator).
 *
 * # Carve-outs
 *
 *   - capability null (probe still pending) → no-op, return
 *     server recommendation unchanged. The probe arrives within
 *     ~50ms typically; if it lands later than the chip render,
 *     the next render reconciles.
 *   - hybridFeasibility null (Firefox / Safari — API absent) →
 *     no-op. We can't say "Bluetooth is off"; we'd just be
 *     hiding a working option for users who have BT on.
 *   - method === fragile_redirect / tap / none → no-op.
 *     Already routes the user off the failing path.
 *
 * Pure function — no IO, no state. Suitable for either lazy
 * derivation in a component or memoized resolution in a hook.
 */
export function reconcileRecommendationWithCapability(
  rec: RecommendedDefault | undefined,
  capability: { hybridFeasibility?: boolean | null; platformAuthenticator?: boolean | null } | null,
): RecommendedDefault | undefined {
  if (!rec || !capability) return rec;

  // Phone-QR + hybrid known-false → demote.
  if (rec.method === "phone_qr" && capability.hybridFeasibility === false) {
    const securityKey = rec.alternatives.find((a) => a.method === "security_key");
    if (securityKey) {
      return {
        ...rec,
        method: "security_key",
        reasonSlot: securityKey.reasonSlot,
        alternatives: rec.alternatives.filter(
          (a) => a.method !== "security_key",
        ),
      };
    }
    // No security-key fallback in the alts — typical for
    // shared_or_kiosk (TAP-only alts). Demote to MethodNone with
    // a Bluetooth-off reason so the EnvironmentPreflightBanner
    // path is invoked.
    return {
      ...rec,
      method: "none",
      reasonSlot: "reason.none_no_webauthn",
      alternatives: [],
    };
  }

  // Platform-passkey + platformAuthenticator known-false → demote.
  // The server thought modern_capable from UA but the device has
  // no UV-capable platform authenticator (Hello not set up, Touch
  // ID not enrolled, screen-lock not configured). Drop to phone-QR
  // if hybrid is viable, else security-key, else MethodNone.
  if (rec.method === "platform_passkey" && capability.platformAuthenticator === false) {
    const phoneOK = capability.hybridFeasibility !== false;
    if (phoneOK) {
      const phoneAlt = rec.alternatives.find((a) => a.method === "phone_qr");
      return {
        ...rec,
        method: "phone_qr",
        reasonSlot: phoneAlt?.reasonSlot ?? "reason.phone_qr_fragile",
        alternatives: rec.alternatives.filter(
          (a) => a.method !== "phone_qr",
        ),
      };
    }
    const keyAlt = rec.alternatives.find((a) => a.method === "security_key");
    if (keyAlt) {
      return {
        ...rec,
        method: "security_key",
        reasonSlot: keyAlt.reasonSlot,
        alternatives: rec.alternatives.filter(
          (a) => a.method !== "security_key",
        ),
      };
    }
    return {
      ...rec,
      method: "none",
      reasonSlot: "reason.none_no_webauthn",
      alternatives: [],
    };
  }

  return rec;
}

/**
 * whatYouEnableSlotFor picks the DD-15 pre-ceremony posture
 * explainer slot from the recommended method. Plain mapping;
 * the protection profile is method-level, not OS-level, so no
 * OS axis is consulted. Returns null for `none` (the dead-end
 * surfaces a banner, not an explainer card).
 */
export function whatYouEnableSlotFor(
  method: RecommendedMethod,
): SlotKey | null {
  switch (method) {
    case "platform_passkey":
      return "what_you_enable.platform_passkey";
    case "phone_qr":
      return "what_you_enable.phone_qr";
    case "security_key":
      return "what_you_enable.security_key";
    case "tap":
      return "what_you_enable.tap";
    case "fragile_redirect":
      return "what_you_enable.fragile_redirect";
    case "none":
    default:
      return null;
  }
}

/**
 * celebrationSlotFor picks the DD-7 celebration slot key from
 * the transport the user just enrolled with + the device-hint
 * slot the DD-2 recommender (or chip) is already aware of.
 *
 * The mapping:
 *   - this_device / internal           → celebration.platform_<os>
 *     where <os> derives from the device-hint slot
 *     (e.g., "device_hint.mac" → mac). If the OS family is
 *     unknown, falls back to celebration.platform_generic so
 *     the user still gets a plausible posture line.
 *   - other_device / hybrid           → celebration.phone_qr
 *   - security_key / usb / nfc / ble  → celebration.security_key
 *
 * Always returns a SlotKey; never null. The caller can
 * unconditionally pass the result to resolveSlot().
 */
export function celebrationSlotFor(
  transport: EnrolledTransport | string | null,
  deviceHintSlot?: SlotKey | string,
): SlotKey {
  // NULL means "the authenticator reported nothing and the user
  // asked for nothing in particular" (the picker's `any` card with
  // an authenticator that exposes neither getTransports() nor an
  // attachment). The generic slot is the only one that does not
  // name a method — going through the default arm below would let
  // the recommender's device hint put "Windows Hello" on a
  // credential we did not observe.
  if (transport === null) return "celebration.platform_generic";
  switch (transport) {
    case "other_device":
    case "hybrid":
      return "celebration.phone_qr";
    case "security_key":
    case "usb":
    case "nfc":
    case "ble":
      return "celebration.security_key";
    case "this_device":
    case "internal":
    default:
      // Platform passkey — pick the OS-specific variant from the
      // device-hint slot the recommender already chose. The
      // device hint encodes OS family (mac/windows/iphone/
      // android/chromeos/linux) for capable + fragile_partial
      // classes; for everything else we fall back to the
      // generic platform celebration.
      switch (deviceHintSlot) {
        case "device_hint.mac":
          return "celebration.platform_mac";
        case "device_hint.windows":
          return "celebration.platform_windows";
        case "device_hint.iphone":
          return "celebration.platform_iphone";
        case "device_hint.ipad":
          return "celebration.platform_ipad";
        case "device_hint.android":
          return "celebration.platform_android";
        case "device_hint.chromeos":
          return "celebration.platform_chromeos";
        case "device_hint.linux":
          return "celebration.platform_linux";
        default:
          return "celebration.platform_generic";
      }
  }
}
